(() => {
  'use strict';
  if (customElements.get('smd-deferred-review')) return;

  const entries = new Map();
  const failures = new Map();
  const styles = new Map();
  const loadStyle = url => {
    if (styles.has(url)) return styles.get(url);
    const existing = [...document.querySelectorAll('link[rel="stylesheet"]')].find(link => link.href === url);
    if (existing) {
      if (existing.sheet) return Promise.resolve();
      const promise = new Promise(resolve => {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', resolve, { once: true });
      });
      styles.set(url, promise);
      return promise;
    }
    const promise = new Promise(resolve => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.onload = link.onerror = resolve;
      document.head.append(link);
    });
    styles.set(url, promise);
    return promise;
  };

  const loadEntry = (base, point, key) => {
    const url = new URL(point, base).href;
    if (entries.has(url)) return entries.get(url);
    const promise = (async () => {
      try {
        const response = await fetch(new URL('manifest.json', base), { cache: 'force-cache' });
        if (!response.ok) throw new Error('Review manifest unavailable');
        const manifest = await response.json();
        const visited = new Set();
        const css = new Set();
        const collect = name => {
          if (visited.has(name)) return;
          visited.add(name);
          const entry = manifest[name];
          entry?.css?.forEach(file => css.add(new URL(file, base).href));
          entry?.imports?.forEach(collect);
        };
        collect(key);
        await Promise.all([...css].map(loadStyle));
      } catch (_) {
        // The official loader also attempts the entry when its manifest is unavailable.
      }
      if ([...document.scripts].some(script => script.src === url)) return;
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        const attemptUrl = new URL(url);
        const retries = failures.get(url) || 0;
        // Browsers cache failed ES-module URLs for the lifetime of the document.
        if (retries) attemptUrl.searchParams.set('smd_retry', String(retries));
        script.src = attemptUrl.href;
        script.onload = resolve;
        script.onerror = () => { script.remove(); reject(new Error('Review module unavailable')); };
        document.head.append(script);
      });
    })();
    entries.set(url, promise);
    promise.catch(() => {
      entries.delete(url);
      failures.set(url, (failures.get(url) || 0) + 1);
    });
    return promise;
  };

  customElements.define('smd-deferred-review', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const revealForHash = hash => {
        if (!hash) return;
        let target;
        try { target = document.getElementById(decodeURIComponent(hash.replace(/^#/, ''))); } catch (_) { return; }
        if (target && this.contains(target)) this.activate();
      };
      document.addEventListener('click', event => {
        const link = event.target.closest?.('a[href]');
        if (link) {
          const url = new URL(link.href, location.href);
          if (url.origin === location.origin && url.pathname === location.pathname) revealForHash(url.hash);
        }
      }, options);
      window.addEventListener('hashchange', () => revealForHash(location.hash), options);
      window.addEventListener('load', () => {
        if (this.waitingForEmbed) this.activate();
      }, { ...options, once: true });
      this.addEventListener('pointerdown', () => {
        this.embedAttempts = 0;
        this.activate();
      }, options);
      this.addEventListener('focusin', () => {
        this.embedAttempts = 0;
        this.activate();
      }, options);
      revealForHash(location.hash);
      if (!('IntersectionObserver' in window)) {
        this.activate();
        return;
      }
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.activate();
      }, { rootMargin: '300px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.reviewObserver?.disconnect();
      cancelAnimationFrame(this.labelFrame);
      this.controller?.abort();
      clearTimeout(this.retryTimer);
    }

    syncReviewLabels() {
      for (const row of this.querySelectorAll('.jm-histogram-row[role="button"]')) {
        // Use the button's visible contents as its name instead of the app's font-glyph label.
        const label = row.getAttribute('aria-label');
        if (label && /[\uE000-\uF8FF]/.test(label)) row.removeAttribute('aria-label');
      }
    }

    async activate() {
      if (!this.isConnected || this.loading || this.loaded) return;
      const base = window.jdgm?.CDN_BASE_URL;
      if (!base) {
        // The app embed may still be loading. Keep server-rendered reviews visible.
        this.waitingForEmbed = true;
        clearTimeout(this.retryTimer);
        this.embedAttempts = (this.embedAttempts || 0) + 1;
        if (this.embedAttempts <= 20) this.retryTimer = setTimeout(() => this.activate(), 500);
        return;
      }
      this.waitingForEmbed = false;
      this.loading = true;
      this.observer?.disconnect();
      try {
        for (const widget of this.querySelectorAll('[data-smd-review-entry-point]')) {
          const point = widget.dataset.smdReviewEntryPoint;
          widget.dataset.entryPoint = point;
          await loadEntry(base, point, widget.dataset.entryKey);
        }
        this.loaded = true;
        if (this.isConnected) {
          this.syncReviewLabels();
          this.reviewObserver?.disconnect();
          this.reviewObserver = new MutationObserver(mutations => {
            if (!mutations.some(mutation => mutation.type !== 'attributes' ||
              (mutation.target.matches('.jm-histogram-row[role="button"]') && mutation.target.hasAttribute('aria-label')))) return;
            if (this.labelFrame) return;
            this.labelFrame = requestAnimationFrame(() => {
              this.labelFrame = 0;
              if (this.isConnected) this.syncReviewLabels();
            });
          });
          // Scope observation to the widget and ignore our own attribute removals.
          this.reviewObserver.observe(this, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['aria-label'] });
        }
      } catch (_) {
        // Static review content remains available; another interaction can retry.
      } finally {
        this.loading = false;
      }
    }
  });
})();
